import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';

const FolderView = ({ folder }) => {
  const { 
    switchToProject, 
    selectFolder, 
    folders,
    createFolder,
    updateFolder,
    deleteFolder,
    deleteProject,
    moveProject,
    createProject
  } = useProject();

  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, type: null, item: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [projectToMove, setProjectToMove] = useState(null);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [parentFolderForCreate, setParentFolderForCreate] = useState(null);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [parentFolderForProject, setParentFolderForProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');

  // Build breadcrumb path by traversing up the tree
  const getBreadcrumbs = () => {
    if (!folder || !folder.id) return [];
    
    const crumbs = [];
    const allFolders = [...(folders.shared || []), ...(folders.personal || [])];
    
    // Helper to find folder and build path
    const findPath = (targetId, folderList, path = []) => {
      for (const f of folderList) {
        const currentPath = [...path, f];
        if (f.id === targetId) {
          return currentPath;
        }
        if (f.children && f.children.length > 0) {
          const found = findPath(targetId, f.children, currentPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findPath(folder.id, allFolders) || [];
  };

  const breadcrumbs = folder?.id ? getBreadcrumbs() : [];

  // Get subfolders and projects for current folder
  const subfolders = folder?.children || [];
  const projects = folder?.projects || [];

  const handleFolderClick = (clickedFolder) => {
    selectFolder(clickedFolder);
  };

  const handleProjectClick = (project) => {
    switchToProject(project.id);
  };

  const handleBreadcrumbClick = (breadcrumbFolder) => {
    selectFolder(breadcrumbFolder);
  };

  const handleRootClick = () => {
    selectFolder(null); // Go to root level
  };

  const handleContextMenu = (e, type, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ show: true, x: e.clientX, y: e.clientY, type, item });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, type: null, item: null });
  };

  const handleCreateSubfolder = () => {
    setParentFolderForCreate(contextMenu.item);
    setNewName('');
    setShowCreateFolderModal(true);
    closeContextMenu();
  };

  const handleCreateProject = () => {
    setParentFolderForProject(contextMenu.item);
    setNewProjectName('');
    setShowCreateProjectModal(true);
    closeContextMenu();
  };

  const handleRenameFolder = () => {
    // Prevent renaming the root Personal Workspace folder (has no parent)
    if (contextMenu.item.isPersonal && !contextMenu.item.parentId) {
      setItemToRename(null);
      setNewName('');
      setShowRenameModal(true); // Will show error in modal
      closeContextMenu();
      return;
    }

    setItemToRename(contextMenu.item);
    setNewName(contextMenu.item.name);
    setShowRenameModal(true);
    closeContextMenu();
  };

  const handleDeleteClick = () => {
    setItemToDelete(contextMenu.item);
    setShowDeleteModal(true);
    closeContextMenu();
  };

  const handleConfirmDelete = async () => {
    try {
      // Check if trying to delete root Personal Workspace (has no parent)
      if (itemToDelete.type === 'folder' && itemToDelete.isPersonal && !itemToDelete.parentId) {
        setShowDeleteModal(false);
        setItemToDelete(null);
        return; // Error shown in modal
      }

      if (itemToDelete.type === 'folder') {
        await deleteFolder(itemToDelete.id);
      } else {
        await deleteProject(itemToDelete.id);
      }
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const handleMoveProjectClick = () => {
    setProjectToMove(contextMenu.item);
    setTargetFolderId(contextMenu.item.folderId);
    setShowMoveProjectModal(true);
    closeContextMenu();
  };

  const handleConfirmMoveProject = async () => {
    try {
      if (!targetFolderId) {
        alert('Please select a folder');
        return;
      }
      await moveProject(projectToMove.id, targetFolderId);
      setShowMoveProjectModal(false);
      setProjectToMove(null);
      setTargetFolderId(null);
    } catch (err) {
      alert(err.message || 'Failed to move project');
    }
  };

  const handleConfirmRename = async () => {
    try {
      if (!itemToRename) {
        // This is the Personal Workspace error case
        setShowRenameModal(false);
        return;
      }

      if (!newName || !newName.trim() || newName.trim() === itemToRename.name) {
        setShowRenameModal(false);
        setItemToRename(null);
        setNewName('');
        return;
      }

      if (itemToRename.type === 'project') {
        // Rename project via ProjectContext
        const { projectService } = await import('../services/api');
        await projectService.updateProject(itemToRename.id, { name: newName.trim() });
        // Reload to reflect changes
        window.location.reload();
      } else {
        // Rename folder
        await updateFolder(itemToRename.id, newName.trim());
      }
      
      setShowRenameModal(false);
      setItemToRename(null);
      setNewName('');
    } catch (err) {
      alert(err.message || `Failed to rename ${itemToRename?.type || 'item'}`);
    }
  };

  const handleConfirmCreateFolder = async () => {
    try {
      if (!newName || !newName.trim()) {
        setShowCreateFolderModal(false);
        return;
      }

      await createFolder(newName.trim(), parentFolderForCreate.id, parentFolderForCreate.isPersonal);
      setShowCreateFolderModal(false);
      setParentFolderForCreate(null);
      setNewName('');
    } catch (err) {
      alert(err.message || 'Failed to create folder');
    }
  };

  const handleConfirmCreateProject = async () => {
    try {
      if (!newProjectName || !newProjectName.trim()) {
        setShowCreateProjectModal(false);
        return;
      }

      await createProject(newProjectName.trim(), parentFolderForProject?.id || folder?.id);
      setShowCreateProjectModal(false);
      setParentFolderForProject(null);
      setNewProjectName('');
    } catch (err) {
      alert(err.message || 'Failed to create project');
    }
  };

  // Helper function to recursively render folder options
  const renderFolderOptions = (folderList, depth = 0) => {
    const options = [];
    const indent = '\u00A0\u00A0'.repeat(depth);

    folderList.forEach(f => {
      options.push(
        <option key={f.id} value={f.id}>
          {indent}{depth > 0 ? '└ ' : ''}{f.name}
        </option>
      );

      if (f.children && f.children.length > 0) {
        options.push(...renderFolderOptions(f.children, depth + 1));
      }
    });

    return options;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--tqa-surface)' }}>
      {/* Breadcrumb Navigation */}
      <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)' }}>
        <nav className="flex items-center space-x-2 text-sm">
          <button
            onClick={handleRootClick}
            className="transition-colors font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
          
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <svg className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.25)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <button
                onClick={() => handleBreadcrumbClick(crumb)}
                className="transition-colors font-medium"
                style={{ color: index === breadcrumbs.length - 1 ? 'var(--tqa-green)' : 'rgba(255,255,255,0.6)', cursor: index === breadcrumbs.length - 1 ? 'default' : 'pointer' }}
                disabled={index === breadcrumbs.length - 1}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
          
          {breadcrumbs.length === 0 && (
            <>
              <svg className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.25)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="font-medium" style={{ color: 'var(--tqa-green)' }}>All Projects</span>
            </>
          )}
        </nav>
      </div>

      {/* Content Grid */}
      <div 
        className="flex-1 overflow-y-auto p-6"
        onContextMenu={(e) => {
          // Check if we're not clicking on a folder or project card
          const clickedOnCard = e.target.closest('[data-item-type]');
          if (!clickedOnCard) {
            e.preventDefault();
            handleContextMenu(e, 'background', { ...folder, type: 'background' });
          }
        }}
      >
        {(subfolders.length === 0 && projects.length === 0) ? (
          <div className="flex flex-col items-center justify-center h-full" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <svg className="w-20 h-20 mb-4" style={{ color: 'rgba(255,255,255,0.15)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-sm font-medium uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>This folder is empty</p>
            <p className="text-xs mb-6" style={{ color: 'rgba(255,255,255,0.3)' }}>Create a new project or subfolder to get started</p>
            
            {/* Action Buttons */}
            <div className="flex gap-4 mt-2">
              <button
                onClick={() => {
                  setParentFolderForCreate(folder);
                  setNewName('');
                  setShowCreateFolderModal(true);
                }}
                className="group flex flex-col items-center justify-center p-6 cursor-pointer transition-all duration-200 hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <svg 
                  className="w-16 h-16 transition-transform duration-200 group-hover:scale-110 mb-3" 
                  style={{ color: 'var(--tqa-green)' }}
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  New Folder
                </span>
              </button>

              <button
                onClick={() => {
                  setParentFolderForProject(folder);
                  setNewProjectName('');
                  setShowCreateProjectModal(true);
                }}
                className="group flex flex-col items-center justify-center p-6 cursor-pointer transition-all duration-200 hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <svg 
                  className="w-16 h-16 transition-transform duration-200 group-hover:scale-110 mb-3" 
                  style={{ color: 'var(--tqa-green)' }}
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  New Project
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            onContextMenu={(e) => {
              // Check if we're not clicking on a folder or project card
              const clickedOnCard = e.target.closest('[data-item-type]');
              if (!clickedOnCard) {
                e.preventDefault();
                handleContextMenu(e, 'background', { ...folder, type: 'background' });
              }
            }}
          >
            {/* Folder Cards */}
            {subfolders.map((subfolder) => (
              <div
                key={subfolder.id}
                data-item-type="folder"
                onClick={(e) => {
                  if (!e.defaultPrevented) handleFolderClick(subfolder);
                }}
                onContextMenu={(e) => handleContextMenu(e, 'folder', { ...subfolder, type: 'folder' })}
                className="group relative p-5 cursor-pointer transition-all duration-200 hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <div className="flex flex-col items-center justify-center space-y-3">
                  {/* Folder Icon */}
                  <div className="relative">
                    <svg 
                      className="w-16 h-16 transition-transform duration-200 group-hover:scale-110" 
                      style={{ color: 'var(--tqa-green)' }}
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                    {subfolder.isPersonal && (
                      <div className="absolute -top-1 -right-1 rounded-full p-1" style={{ backgroundColor: 'var(--tqa-green)' }}>
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Folder Name */}
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-center line-clamp-2 transition-colors" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    {subfolder.name}
                  </h3>
                  
                  {/* Folder Stats */}
                  <div className="flex items-center space-x-3 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {(subfolder.children?.length || 0) > 0 && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                        <span>{subfolder.children.length}</span>
                      </div>
                    )}
                    {(subfolder.projects?.length || 0) > 0 && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <span>{subfolder.projects.length}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Project Cards */}
            {projects.map((project) => (
              <div
                key={project.id}
                data-item-type="project"
                onClick={(e) => {
                  if (!e.defaultPrevented) handleProjectClick(project);
                }}
                onContextMenu={(e) => handleContextMenu(e, 'project', { ...project, type: 'project' })}
                className="group relative p-5 cursor-pointer transition-all duration-200 hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <div className="flex flex-col items-center justify-center space-y-3">
                  {/* Project Icon */}
                  <div className="relative">
                    <svg 
                      className="w-16 h-16 transition-transform duration-200 group-hover:scale-110" 
                      style={{ color: 'var(--tqa-green)' }}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                      />
                    </svg>
                  </div>
                  
                  {/* Project Name */}
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-center line-clamp-2 transition-colors" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    {project.name}
                  </h3>
                  
                  {/* Project Info */}
                  <div className="flex flex-col items-center space-y-1 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {project.createdAt && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{new Date(project.createdAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Hover indicator */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-5 h-5" style={{ color: 'var(--tqa-green)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu.show && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 py-1 min-w-[160px]"
            style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px`, backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
          >
            {contextMenu.type === 'background' && (
              <>
                <button
                  onClick={handleCreateSubfolder}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  New Subfolder
                </button>
                <button
                  onClick={handleCreateProject}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  New Project
                </button>
              </>
            )}
            {contextMenu.type === 'folder' && (
              <>
                <button
                  onClick={handleCreateSubfolder}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  New Subfolder
                </button>
                <button
                  onClick={handleCreateProject}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  New Project
                </button>
                <div className="my-1" style={{ borderTop: '1px solid var(--tqa-border-dark)' }} />
                <button
                  onClick={handleRenameFolder}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Rename
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-110"
                  style={{ color: 'var(--tqa-red)' }}
                >
                  Delete
                </button>
              </>
            )}
            {contextMenu.type === 'project' && (
              <>
                <button
                  onClick={handleRenameFolder}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Rename
                </button>
                <button
                  onClick={handleMoveProjectClick}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Move to Folder
                </button>
                <div className="my-1" style={{ borderTop: '1px solid var(--tqa-border-dark)' }} />
                <button
                  onClick={handleDeleteClick}
                  className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider transition-colors hover:brightness-110"
                  style={{ color: 'var(--tqa-red)' }}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">
              Delete {itemToDelete.type === 'folder' ? 'Folder' : 'Project'}
            </h3>
            {itemToDelete.type === 'folder' && itemToDelete.isPersonal && !itemToDelete.parentId && itemToDelete.name === 'Personal Workspace' ? (
              <>
                <p className="text-sm mb-6" style={{ color: 'var(--tqa-red)' }}>
                  The Personal Workspace folder cannot be deleted.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setItemToDelete(null);
                    }}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Are you sure you want to delete <strong>"{itemToDelete.name}"</strong>?
                  <br /><br />
                  {itemToDelete.type === 'folder' && 'This will delete all contents including subfolders and projects. '}
                  This action cannot be undone.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setItemToDelete(null);
                    }}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110"
                    style={{ backgroundColor: 'var(--tqa-red)', color: 'white' }}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Move Project Modal */}
      {showMoveProjectModal && projectToMove && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Move Project</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Move <strong style={{ color: 'white' }}>"{projectToMove.name}"</strong> to a different folder:
            </p>
            <div className="mb-6">
              <label htmlFor="move-folder" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Select Folder
              </label>
              <select
                id="move-folder"
                value={targetFolderId || ''}
                onChange={(e) => setTargetFolderId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
              >
                <option value="">Select a folder</option>
                {folders.shared && folders.shared.length > 0 && (
                  <optgroup label="Shared Folders">
                    {renderFolderOptions(folders.shared, 0)}
                  </optgroup>
                )}
                {folders.personal && folders.personal.length > 0 && (
                  <optgroup label="Personal Workspace">
                    {renderFolderOptions(folders.personal, 0)}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowMoveProjectModal(false);
                  setProjectToMove(null);
                  setTargetFolderId(null);
                }}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMoveProject}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!targetFolderId}
              >
                Move Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">
              Rename {itemToRename?.type === 'project' ? 'Project' : 'Folder'}
            </h3>
            {!itemToRename ? (
              <>
                <p className="text-sm mb-6" style={{ color: 'var(--tqa-red)' }}>
                  The Personal Workspace folder cannot be renamed.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRenameModal(false);
                      setItemToRename(null);
                      setNewName('');
                    }}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-6">
                  <label htmlFor="rename-folder-name" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Folder Name
                  </label>
                  <input
                    type="text"
                    id="rename-folder-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleConfirmRename();
                      }
                    }}
                    className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                    style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                    placeholder="Enter folder name"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRenameModal(false);
                      setItemToRename(null);
                      setNewName('');
                    }}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                    style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmRename}
                    className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                    style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                    disabled={!newName || !newName.trim() || newName.trim() === itemToRename.name}
                  >
                    Rename
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && parentFolderForCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Create Subfolder</h3>
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Creating subfolder in <strong style={{ color: 'rgba(255,255,255,0.7)' }}>"{parentFolderForCreate.name}"</strong>
            </p>
            <div className="mb-6">
              <label htmlFor="new-folder-name" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Folder Name
              </label>
              <input
                type="text"
                id="new-folder-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateFolder();
                  }
                }}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                placeholder="Enter folder name"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setParentFolderForCreate(null);
                  setNewName('');
                }}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateFolder}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!newName || !newName.trim()}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateProjectModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="p-6 w-96" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
            <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Create Project</h3>
            <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Creating project in <strong style={{ color: 'rgba(255,255,255,0.7)' }}>"{parentFolderForProject?.name || folder?.name}"</strong>
            </p>
            <div className="mb-6">
              <label htmlFor="new-project-name" className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Project Name
              </label>
              <input
                type="text"
                id="new-project-name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateProject();
                  }
                }}
                className="w-full px-3 py-2 text-sm text-white focus:outline-none"
                style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                placeholder="Enter project name"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateProjectModal(false);
                  setParentFolderForProject(null);
                  setNewProjectName('');
                }}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateProject}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: 'var(--tqa-black)' }}
                disabled={!newProjectName || !newProjectName.trim()}
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderView;
